import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { CheckCircle2, AlertCircle, Loader2, X } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

export const ChangePasswordForm = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isValidLength, setIsValidLength] = useState(false);

  useEffect(() => {
    setIsValidLength(newPassword.length >= 6);
    setStatus('idle');
    setErrorMessage(null);
  }, [newPassword]);

  const handlePasswordChange = async () => {
    if (!user?.email) {
      setStatus('error');
      setErrorMessage(t('profile.security.change_password.errors.session_error'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatus('error');
      setErrorMessage(t('profile.security.change_password.errors.passwords_dont_match'));
      return;
    }

    if (newPassword.length < 6) {
      setStatus('error');
      setErrorMessage(t('profile.security.change_password.errors.password_too_short'));
      return;
    }

    if (currentPassword === newPassword) {
      setStatus('error');
      setErrorMessage(t('profile.security.change_password.errors.same_password'));
      return;
    }

    try {
      setLoading(true);
      setStatus('idle');
      setErrorMessage(null);
      console.log('Début de la procédure de changement de mot de passe...');

      // D'abord, on vérifie le mot de passe actuel
      console.log('Vérification du mot de passe actuel...');
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      });

      console.log('Résultat de la vérification:', { signInError, hasUser: !!signInData?.user });

      if (signInError || !signInData.user) {
        setStatus('error');
        setErrorMessage(t('profile.security.change_password.errors.incorrect_password'));
        return;
      }

      // Si le mot de passe actuel est correct, on peut mettre à jour le nouveau
      console.log('Mise à jour du mot de passe...');
      const { data: updateData, error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      console.log('Résultat de la mise à jour:', { updateError, hasUser: !!updateData?.user });

      if (updateError) {
        throw updateError;
      }

      if (!updateData.user) {
        throw new Error('Aucune confirmation de mise à jour reçue');
      }

      console.log('Mot de passe mis à jour avec succès');
      setStatus('success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Erreur détaillée lors du changement de mot de passe:', error);
      setStatus('error');
      setErrorMessage(error.message || t('profile.security.change_password.errors.generic_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg p-6">
      <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
        {t('profile.security.change_password.title')}
      </h3>
      <div className="space-y-4">
        <div>
          <label htmlFor="current-password" className="block text-sm font-medium text-gray-700">
            {t('profile.security.change_password.current_password')}
          </label>
          <input
            type="password"
            id="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
            {t('profile.security.change_password.new_password')}
          </label>
          <div className="mt-1">
            <input
              type="password"
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className={`block w-full rounded-md shadow-sm focus:ring-indigo-500 sm:text-sm ${
                newPassword.length > 0
                  ? isValidLength
                    ? 'border-green-300 focus:border-green-500'
                    : 'border-red-300 focus:border-red-500'
                  : 'border-gray-300 focus:border-indigo-500'
              }`}
            />
            <div className="mt-1 flex items-center gap-2">
              {newPassword.length > 0 && (
                <>
                  {isValidLength ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                  <span className={`text-sm ${isValidLength ? 'text-green-600' : 'text-red-600'}`}>
                    {t('profile.security.change_password.min_length')}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
            {t('profile.security.change_password.confirm_password')}
          </label>
          <input
            type="password"
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 sm:text-sm ${
              confirmPassword.length > 0
                ? confirmPassword === newPassword
                  ? 'border-green-300 focus:border-green-500'
                  : 'border-red-300 focus:border-red-500'
                : 'border-gray-300 focus:border-indigo-500'
            }`}
          />
        </div>

        {status === 'success' && (
          <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-4 py-3 rounded-md">
            <CheckCircle2 className="w-5 h-5" />
            <span>{t('profile.security.change_password.success_message')}</span>
          </div>
        )}

        {status === 'error' && errorMessage && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-md">
            <AlertCircle className="w-5 h-5" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div>
          <button
            type="button"
            onClick={handlePasswordChange}
            disabled={loading || !isValidLength || newPassword !== confirmPassword}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('profile.security.change_password.updating')}
              </span>
            ) : (
              t('profile.security.change_password.submit_button')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}; 